/** @jsx isolateComponent */
import { useEffect, useState } from 'react';
import { css } from '@emotion/core';
import DayPicker from 'react-day-picker';

import { useClickOutside } from '../../../hooks';
import { Colors, Zindexes } from '../../../theme';
import { Button } from '../../navigation/Button';
import { ButtonNav } from '../../navigation/ButtonNav';
import { FloatingBox } from '../../display/FloatingBox';

import { DateRangePickerInput } from './DateRangePickerInput';
import { DateRangePickerNavbar } from './DateRangePickerNavbar';
import { WithDayPickerStyle } from './WithDayPickerStyle';
import { DayClassNames, DayModifiers, DateOption, DateRange } from './types';
import { calculateNextRange, isSameDay } from './utils';

import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import type { RangeModifier } from 'react-day-picker/types/Modifiers';

type Props = {
  /**
   * Accessibility label that describes this component. This should be supplied
   * if no corresponding `label` element is used.
   */
  ariaLabel: string;
  /** Text of close button */
  closeText: string;
  /** Disabled Dates */
  disabledDates?: DayModifiers;
  /** HTML `id` attribute for the element, used to associate it with a `label`. */
  id?: string;
  /** Size of input */
  inputSize?: 'small' | 'medium';
  /** Disables the input and makes it non-interactive. */
  isDisabled?: boolean;
  /** Indicates the field has an error */
  isErrored?: boolean;
  /** Alignment of picker menu */
  menuAlign: 'left' | 'right' | 'stretch';
  /** ariaLabel of menu component */
  menuAriaLabel: string;
  /** callback fired when the value of the input changes */
  onChange: (value: DateRange) => void;
  /** an optional list of predefined DateRanges the user can select from */
  options: Array<DateOption>;
  /** A hint to the user of what can be entered in the control. */
  placeholder?: string;
  /** rendered size of menu */
  size: 'small' | 'medium' | 'large';
  /** value of the input */
  value: DateRange;
};

const Styles = labelStyles('DateRangePicker', {
  root: css`
    position: relative;
  `,

  menu: (props: Props) => [
    css`
      display: grid;
      gap: 20px;
      padding: 20px;
    `,
    props.size === 'large' &&
      css`
        grid-template-columns: auto 1fr;
      `,
  ],

  options: (props: Props) => [
    props.size === 'large' &&
      css`
        border-right: 1px solid ${Colors.grey100};
        padding-right: 20px;
      `,
  ],

  doneButton: css`
    display: flex;
    justify-content: flex-end;
    grid-column: 1 / -1;
  `,

  modalBackdrop: css`
    background: rgba(0, 0, 0, 0.8);
    position: fixed;
    z-index: ${Zindexes.modalOverlay};
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  `,

  modal: css`
    background: ${Colors.white};
    border-radius: 10px;
    overflow: auto;
    width: 100%;
    max-width: 450px;
    max-height: 100vh;
  `,
});

const defaultValue = {};

export const DateRangePicker = (props: Props) => {
  const [selectedOption, setSelectedOption] = useState<DateOption | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState<DateRange>(props.value || defaultValue);
  const { from, to } = value;
  const clampedSize = props.size === 'small' ? props.size : 'medium';
  const isSmall = props.size === 'small';

  useEffect(() => {
    if (props.value !== value) {
      // prop value was changed so updated internal value to match
      setValue(props.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value]);

  const clearSelection = () => {
    setValue(defaultValue);
    props.onChange(defaultValue);
  };

  const closeMenu = () => {
    // Slight hack: Wait until next tick so click events have propagated
    // all the way up the DOM tree. Without this delay, the outside click
    // will call `closeMenu` which will immediately set `isOpen` to `false`.
    // The event will still be propagating and the the event handler that
    // that conditionally calls `openMenu` will be called, and its `isOpen`
    // condition will cause `openMenu` to be called, immediately reopening
    // the menu after it was closed.
    setTimeout(() => {
      setIsOpen(false);
    }, 0);

    // When menu closes fire onChange if the user changed the value
    if (
      !isSameDay(from, props.value?.from) ||
      !isSameDay(to, props.value?.to)
    ) {
      props.onChange(value);
    }
  };

  const openMenu = () => {
    setIsOpen(true);
  };

  const handleDayClick = (
    day: Date,
    modifiers: { [key: string]: any },
    classNames: DayClassNames
  ) => {
    // React-date-picker adds class name to disabled days
    // Emotion updates the class name, so we are checking for that updapted Class Name
    const disabledModifier = classNames.disabled;
    if (modifiers[disabledModifier]) {
      return;
    }

    const nextRange = calculateNextRange(day, from, to);
    setValue(nextRange);
    // Clear any predefined option the user might have selected
    if (selectedOption !== null) {
      setSelectedOption(null);
    }
  };

  const handleSelectOption = (option: DateOption, event: React.MouseEvent) => {
    event.preventDefault();
    setSelectedOption(option);
    setValue(option.value);
  };

  const [setClickOutsideNode] = useClickOutside({
    onClickOutside: closeMenu,
  });

  const menuInner = isOpen && (
    <div aria-label={props.menuAriaLabel} css={Styles.menu(props)}>
      {props.options.length > 0 && (
        <div css={Styles.options(props)}>
          <ButtonNav
            ariaLabel="Predefined date ranges"
            isButtonFullWidth={true}
            items={props.options}
            onChooseItem={handleSelectOption}
            perRow={props.size === 'medium' ? undefined : 1}
            selectedItemKey={selectedOption?.key}
          />
        </div>
      )}
      <WithDayPickerStyle size={clampedSize}>
        {({ classNames, modifiersStyles }) => (
          <DayPicker
            classNames={classNames}
            disabledDays={props.disabledDates}
            fixedWeeks={true}
            initialMonth={from}
            modifiers={value}
            modifiersStyles={modifiersStyles}
            navbarElement={DateRangePickerNavbar}
            numberOfMonths={isSmall ? 1 : 2}
            onDayClick={(day, modifiers) =>
              handleDayClick(day, modifiers, classNames)
            }
            selectedDays={[from as Date, value as RangeModifier]}
          />
        )}
      </WithDayPickerStyle>
      <div css={Styles.doneButton}>
        <Button isFullWidth={props.size === 'small'} onClick={closeMenu}>
          {props.closeText}
        </Button>
      </div>
    </div>
  );

  return (
    <div css={Styles.root}>
      <DateRangePickerInput
        {...props}
        clearSelection={clearSelection}
        isOpen={isOpen}
        onClick={() => {
          if (!props.isDisabled && !isOpen) {
            openMenu();
          }
        }}
        options={props.options}
        size={props.inputSize || clampedSize}
        value={value}
      />
      {isOpen &&
        (isSmall ? (
          // TODO: Replace with `Lightbox` once that component is graduated.
          <div css={Styles.modalBackdrop}>
            <div css={Styles.modal} ref={setClickOutsideNode}>
              {menuInner}
            </div>
          </div>
        ) : (
          <FloatingBox
            align={props.menuAlign}
            intersectHeight={460}
            onClickOutside={closeMenu}
          >
            {menuInner}
          </FloatingBox>
        ))}
    </div>
  );
};

DateRangePicker.defaultProps = {
  closeText: 'Done',
  options: [],
  menuAlign: 'left',
  size: 'medium',
};
